/**
 * The Defender board's URL vocabulary (dashboard design rules §4.2): one place
 * for the route, the section anchors and the search params the L1 sections and
 * their drill-downs share, so a KPI tile, a donut segment and a "View data"
 * affordance can never disagree about where they point.
 *
 * **What is deliberately absent, and cannot be added here.** There is no
 * `?user=`, `?device=`, `?mailbox=`, `?sensor=` or `?incident=` param, and there
 * is nowhere for one to come from: the backend publishes no user, device,
 * mailbox or incident identifier at all (`nova.defender.models` — "not a filter
 * applied on the way out, but the absence of anywhere to put one"), and no
 * `/defender/*` route takes a person, a device or an id as a parameter. A param
 * that could name a person would have nothing to query and nothing to show, so
 * adding one would be a promise this board must not make.
 *
 * Every drill on this board is therefore an **aggregate** drill: a breakdown's
 * table twin (`?data=<chart-id>`, owned by `@/components/board`) opens the exact
 * buckets behind a chart as a grid. The board is a single route: its drill state
 * lives in search params on that route, so back, refresh and link-sharing all
 * work without an L2 sub-route.
 */

/** The L1 board. */
export const DEFENDER_PATH = "/defender";

/** In-page anchor: the consent / setup panel. The board's primary state today. */
export const SETUP_ANCHOR = "defender-setup";
/** In-page anchor: open incidents by severity, age and trend. */
export const INCIDENTS_ANCHOR = "defender-incidents";
/** In-page anchor: alert volume and its mix. */
export const ALERTS_ANCHOR = "defender-alerts";
/** In-page anchor: Microsoft Secure Score and its worst controls. */
export const SECURE_SCORE_ANCHOR = "defender-secure-score";
/** In-page anchor: Defender-for-Identity sensor and agent health. */
export const IDENTITY_ANCHOR = "defender-identity";

/**
 * Board-unique ids for the chart table twins (`?data=<id>`).
 *
 * Collected here rather than spelled inline at each chart because they share one
 * URL namespace: two charts with the same id would open each other's grid.
 */
export const TWIN_IDS = {
  incidentSeverity: "defender-incident-severity",
  incidentStatus: "defender-incident-status",
  incidentClassification: "defender-incident-classification",
  incidentDetermination: "defender-incident-determination",
  incidentServiceSource: "defender-incident-service-source",
  incidentDetectionSource: "defender-incident-detection-source",
  incidentCategory: "defender-incident-category",
  incidentAge: "defender-incident-age",
  incidentCloseTime: "defender-incident-close-time",
  incidentTrend: "defender-incident-trend",
  alertSeverity: "defender-alert-severity",
  alertStatus: "defender-alert-status",
  alertDetermination: "defender-alert-determination",
  alertServiceSource: "defender-alert-service-source",
  alertDetectionSource: "defender-alert-detection-source",
  alertCategory: "defender-alert-category",
  alertTechnique: "defender-alert-technique",
  alertTrend: "defender-alert-trend",
  scoreTrend: "defender-score-trend",
  worstControls: "defender-worst-controls",
  controlEffort: "defender-control-effort",
  sensorHealth: "defender-sensor-health",
  sensorType: "defender-sensor-type",
  sensorDeployment: "defender-sensor-deployment",
  issueSeverity: "defender-issue-severity",
  issueType: "defender-issue-type",
} as const;
